import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Pagination,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { auditLogService, LoginHistory } from '../../services/auditLogService';
import axios from 'axios';
import { API_BASE_URL } from '../../config/constants';

interface UserLoginHistoryProps {
  userId: string;
  userName?: string;
}

const UserLoginHistory: React.FC<UserLoginHistoryProps> = ({ 
  userId,
  userName
}) => {
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const limit = 10;

  useEffect(() => {
    loadLoginHistory();
  }, [userId, page]);

  const loadLoginHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Direct API call
      try {
        const token = localStorage.getItem('accessToken');
        const directResponse = await axios.get(
          `${API_BASE_URL}/admin/audit-logs/user/${userId}/logins`,
          {
            params: { page, limit },
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        // Use direct response data if available
        if (directResponse.data?.status === 'success' && Array.isArray(directResponse.data?.data)) {
          setLoginHistory(directResponse.data.data);
          if (directResponse.data.pagination) {
            setTotalPages(directResponse.data.pagination.pages || 1);
          }
          setLoading(false);
          return;
        }
      } catch (directError) {
        console.error('Error in direct API call:', directError);
      }
      
      // 2. Fall back to service call
      const response = await auditLogService.getUserLoginHistory(userId, page, limit);
      setLoginHistory(response.data);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      console.error('Error loading login history:', err);
      setError('خطا در بارگذاری تاریخچه ورود');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const formatDate = (dateString: string): string => {
    return auditLogService.formatDate(dateString);
  };

  if (loading && loginHistory.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (loginHistory.length === 0 && !loading) {
    return (
      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
        هیچ تاریخچه ورودی برای این کاربر یافت نشد.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        تاریخچه ورود {userName ? `کاربر ${userName}` : ''}
      </Typography>
      
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>وضعیت</TableCell>
                <TableCell>تاریخ و زمان</TableCell>
                <TableCell>آدرس IP</TableCell>
                <TableCell>مرورگر</TableCell>
                <TableCell>دستگاه</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loginHistory.map((item) => (
                <TableRow key={item._id} sx={{ 
                  backgroundColor: item.status === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'
                }}>
                  <TableCell>
                    <Chip 
                      icon={item.status === 'success' ? <LoginIcon /> : <LogoutIcon />}
                      label={item.status === 'success' ? 'موفق' : 'ناموفق'} 
                      size="small" 
                      color={item.status === 'success' ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell dir="ltr">{formatDate(item.createdAt)}</TableCell>
                  <TableCell dir="ltr">{item.ip}</TableCell>
                  <TableCell>{item.browser}</TableCell>
                  <TableCell>{item.device}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange} 
            color="primary" 
          />
        </Box>
      )}
    </Box>
  );
};

export default UserLoginHistory;

// UserLoginHistoryButton component to open the dialog
interface UserLoginHistoryButtonProps {
  userId: string;
  userName?: string;
  buttonLabel?: string;
  showIcon?: boolean;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'inherit';
}

export const UserLoginHistoryButton: React.FC<UserLoginHistoryButtonProps> = ({ 
  userId, 
  userName,
  buttonLabel = "تاریخچه ورود",
  showIcon = true,
  variant = 'outlined',
  size = 'small',
  color = 'info'
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <>
      {/* Button or IconButton based on whether buttonLabel is empty */}
      {buttonLabel ? (
        <Button
          variant={variant}
          size={size}
          color={color}
          onClick={handleOpenDialog}
          startIcon={showIcon ? <LoginIcon /> : undefined}
        >
          {buttonLabel}
        </Button>
      ) : (
        <IconButton
          size={size === 'large' ? 'medium' : size === 'medium' ? 'small' : 'small'}
          color={color}
          onClick={handleOpenDialog}
        >
          <LoginIcon fontSize="small" />
        </IconButton>
      )}

      {/* Dialog containing the UserLoginHistory component */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          تاریخچه ورود {userName ? `کاربر ${userName}` : ''}
          <Tooltip title="بستن">
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          <UserLoginHistory userId={userId} userName={userName} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            بستن
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 